import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { RefreshTokenModel } from '@core/auth/models/refresh-token.model';
import { LoginService } from '@core/auth/services/login.service';
import { TokenProcessingService } from '@core/auth/services/token-processing.service';
import { AppconfigService } from '@core/services/utility/appconfig.service';
import { CammsClientStorageService } from 'projects/camms-client-storage/src/public-api';
import {
  BehaviorSubject,
  Observable,
  delay,
  filter,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { catchError } from 'rxjs/internal/operators/catchError';
import { environment } from '@environments/environment';
import { appCodes } from '@core/constants/app-codes.constant';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const loginService = inject(LoginService);
  const httpBackend = inject(HttpBackend);
  const tokenProcessingService = inject(TokenProcessingService);
  const storageService = inject(CammsClientStorageService);
  const appConfigService = inject(AppconfigService);

  let invalidTokenAttempts = 0;
  let isRefreshing = false;
  let refreshTokenSubject: BehaviorSubject<RefreshTokenModel> = new BehaviorSubject<RefreshTokenModel>({} as RefreshTokenModel);

  let authMetod = appConfigService.appsettings?.authentcationMethod;

  if (
    !request.url
      .toLocaleLowerCase()
      .includes('authenticatewindowsuser') &&
    !(
      (request.url
        .toLocaleLowerCase()
        .includes('quickupdatesummery') ||
        request.url.toLocaleLowerCase().includes('token')) &&
      authMetod.toLocaleLowerCase() === 'windows'
    )
  ) {
    let tokenCode =
      storageService.getData(appCodes.auth.accessToken) ??
      'TOKEN_IS_NOT_AVAILABLE';
    tokenCode = tokenCode.replace(/[""]/g, '');

    let accessToken = '';

    const tokens = tokenProcessingService.getAuthToken();

    if (
      request.url
        .toLocaleLowerCase()
        .includes('authenticatewithclassictoken')
    ) {
      accessToken = `Bearer ${'TOKEN_IS_NOT_AVAILABLE'}`;
    } else if (tokens && tokens?.length > 0) {
      accessToken = `Bearer ${tokens[0]}`;
    } else if (tokenCode && tokenCode != 'TOKEN_IS_NOT_AVAILABLE') {
      accessToken = `Bearer ${tokenCode}`;
    } else {
      accessToken = `Bearer ${'TOKEN_IS_NOT_AVAILABLE'}`;
    }

    if (request.url && request.url.includes('AuthenticateWithToken')) {
      accessToken = `Bearer ${tokenCode!}`;
    }

    request = request.clone({
      headers: request.headers
        .set('Content-Type', 'application/json')
        .set('Access-Control-Allow-Origin', '*')
        .set('Authorization', accessToken),
    });
  }

  const handle401Error = (
    request: HttpRequest<any>,
    next: HttpHandlerFn,
  ): Observable<any> => {
    invalidTokenAttempts++;

    if (
      !isRefreshing &&
      loginService.getAccessTokenFromLocal() != ''
    ) {
      isRefreshing = true;
      refreshTokenSubject.next({} as RefreshTokenModel);
      return tryGetRefreshTokenService().pipe(
        switchMap((token) => {
          isRefreshing = false;
          refreshTokenSubject.next(token);
          return next(
            addToken(
              request,
              tokenProcessingService.getAuthToken()[0],
              token.refreshToken ||
              token.data.refreshToken ||
              storageService.getData(
                appCodes.auth.refreshToken,
              ),
            ),
          );
        }),
        catchError((err) => {
          // this.loginService.logout(true).subscribe();
          // cannot refresh log out proceed
          return throwError(() => err);
        }),
      );
    } else {
      return refreshTokenSubject.pipe(
        delay(100),
        filter(
          (token) =>
            token != null || token != ({} as RefreshTokenModel),
        ),
        switchMap((jwt) => {
          // if (!jwt.accessToken || jwt.refreshToken) {
          //     return next.handle(request);
          // }
          return next(
            addToken(
              request,
              tokenProcessingService.getAuthToken()[0],
              jwt.refreshToken ||
              storageService.getData(
                appCodes.auth.refreshToken,
              )!,
            ),
          );
        }),
      );
    }
  }

  const tryGetRefreshTokenService = (): Observable<any> => {
    var tempAccessToken = storageService
      .getData(appCodes.auth.accessToken)
      ?.replace(/[""]/g, '')!;
    var tempRefreshToken = storageService
      .getData(appCodes.auth.refreshToken)
      ?.replace(/[""]/g, '')!;

    //if(!tempRefreshToken || tempRefreshToken == null)
    //window.location.reload();
    const httpClient = new HttpClient(httpBackend);
    return httpClient.post<any>(
      environment.commonBaseUrl + '/api/v1/User/Token',
      {
        accessToken: tempAccessToken,
        refreshToken: tempRefreshToken,
        appCode: 'IRM',
      },
    );
  }

  const addToken = (
    request: HttpRequest<any>,
    accessToken: string,
    refreshToken: string,
  ): HttpRequest<any> => {
    loginService.setTokens(accessToken, refreshToken);
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }


  return next(request).pipe(
    // delay(100), //to reduce the chance of picking the same token in different tabs before retrying the failed api call
    catchError((error) => {
      if (invalidTokenAttempts >= 20) {
        loginService.logout(true).subscribe(() => { });
        return throwError(() => error);
      }

      if (
        error instanceof HttpErrorResponse &&
        error.status === 401
      ) {
        return handle401Error(request, next);
      } else if (
        error instanceof HttpErrorResponse &&
        error.status === 500 &&
        request.url &&
        request.url.includes('AuthenticateWithToken')
      ) {
        loginService.logout(true).subscribe(() => { });
        return throwError(() => error);
      } else if (
        error instanceof HttpErrorResponse &&
        error.status === 500 &&
        error.error.meta.isInvalidToken
      ) {
        return handle401Error(request, next);
      } else {
        return throwError(() => error);
      }
    }),
    tap((event) => {
      invalidTokenAttempts = 0;
      
      if (event instanceof HttpResponse) {
        const xToken = event.headers.get('X-Token');
        const urlLower = event.url?.toLowerCase() || '';

        if (
          environment.isOneTimeTokenEnabled &&
          xToken &&
          !urlLower.includes('users/logout') &&
          !urlLower.includes('settings/app-settings.json')
        ) {
          tokenProcessingService.processSecuredTokenPool(xToken);
          tokenProcessingService.storeNewToken(xToken);

          if (
            tokenProcessingService.isTokenPoolThresholdReached() &&
            !request.url.includes('User/UserLogin') &&
            !request.url.includes('User/AuthenticateWithToken')
          ) {
            tokenProcessingService.tryGetRefreshTokenService().subscribe(() => { });
          }
        }
      }
    }),
  );
};